import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { esqueciSenha } from '../../services/senhaService';
import './EsqueciSenha.css';

interface FormErrors {
  email?: string;
  general?: string;
}

function EsqueciSenha() {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultado, setResultado] = useState<{ tipo: string; mensagem: string } | null>(null);

  function validateForm(): FormErrors {
    const newErrors: FormErrors = {};

    if (!email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (email.length > 254) {
      newErrors.email = 'E-mail deve ter no máximo 254 caracteres';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Formato de e-mail inválido';
    }

    return newErrors;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const response = await esqueciSenha(email);

      if (response.status === 429) {
        setErrors({
          general: 'Limite de solicitações atingido. Tente novamente em 15 minutos.',
        });
        return;
      }

      if (response.tipo && response.mensagem) {
        setResultado({ tipo: response.tipo, mensagem: response.mensagem });
      } else {
        setResultado({ tipo: 'enviado', mensagem: 'Solicitação processada.' });
      }
    } catch {
      setErrors({ general: 'Erro de conexão. Tente novamente.' });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (resultado) {
    const isSuccess = resultado.tipo === 'enviado';
    const iconColor = isSuccess ? '#4caf50' : '#c9a96e';

    return (
      <div className="esqueci-senha-page">
        <div className="esqueci-senha-card">
          <header className="esqueci-senha-card__header">
            <h1 className="esqueci-senha-card__title">
              {isSuccess ? 'E-mail Enviado' : 'Atenção'}
            </h1>
          </header>

          <div className="esqueci-senha-card__success" role="status">
            {isSuccess ? (
              <svg
                className="esqueci-senha-card__success-icon"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" stroke={iconColor} strokeWidth="2" />
                <path d="M8 12l2.5 2.5L16 9" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg
                className="esqueci-senha-card__success-icon"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" stroke={iconColor} strokeWidth="2" />
                <path d="M12 8v4M12 16h.01" stroke={iconColor} strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
            <p className="esqueci-senha-card__success-message">
              {resultado.mensagem}
            </p>
          </div>

          <footer className="esqueci-senha-card__footer">
            <Link to="/" className="esqueci-senha-card__link">
              ← Voltar para o login
            </Link>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="esqueci-senha-page">
      <div className="esqueci-senha-card">
        <header className="esqueci-senha-card__header">
          <h1 className="esqueci-senha-card__title">Esqueci minha senha</h1>
          <p className="esqueci-senha-card__subtitle">
            Informe seu e-mail para receber as instruções de redefinição de senha.
          </p>
        </header>

        <form className="esqueci-senha-form" onSubmit={handleSubmit} noValidate>
          {errors.general && (
            <div className="esqueci-senha-form__error--general" role="alert">
              {errors.general}
            </div>
          )}

          <div className="esqueci-senha-form__field">
            <label className="esqueci-senha-form__label" htmlFor="esqueci-email">
              E-mail
            </label>
            <input
              id="esqueci-email"
              className="esqueci-senha-form__input"
              type="email"
              placeholder="seu@email.com"
              maxLength={254}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
              }}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'esqueci-email-error' : undefined}
              autoComplete="email"
            />
            {errors.email && (
              <span id="esqueci-email-error" className="esqueci-senha-form__error" role="alert">
                {errors.email}
              </span>
            )}
          </div>

          <button
            type="submit"
            className="esqueci-senha-form__submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Enviando...' : 'Enviar instruções'}
          </button>
        </form>

        <footer className="esqueci-senha-card__footer">
          <Link to="/" className="esqueci-senha-card__link">
            ← Voltar para o login
          </Link>
        </footer>
      </div>
    </div>
  );
}

export default EsqueciSenha;
